import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { TextFieldProps } from '@mui/material'
import { LocalProps } from '../../formik/numberField/NumberField'
import { DecimalField } from './DecimalField'
import { BaseNumberField } from './BaseNumberField'

type TextFieldOverrides = Omit<TextFieldProps, 'name' | 'type'>
type DecimalFieldProps = Partial<Omit<LocalProps, 'name' | keyof TextFieldProps>>

type Props = {
  name: string
  suffix?: string
  isDecimal?: boolean
  propsOnChange?: (newValue: number | string) => void
} & TextFieldOverrides &
  DecimalFieldProps

/**
 * MUI NumberField with react hook form
 */
const NumberField: React.FC<Props> = ({ name, isDecimal = false, suffix, propsOnChange, fullWidth, ...props }) => {
  const { control } = useFormContext()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, onChange: (value: number | string) => void): void => {
    const value = e.target.valueAsNumber

    if (isNaN(value)) {
      onChange('')
      return
    }
    onChange(value)
    propsOnChange?.(value)
  }

  if (isDecimal) {
    const { onChange, value, label, helperText, ref, ...restProps } = props
    return <DecimalField name={name} label={label} suffix={suffix} propsOnChange={propsOnChange} fullWidth={fullWidth} {...restProps} />
  }

  const { ref, ...restProps } = props
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, ...field }, fieldState: { error } }) => (
        <BaseNumberField
          {...field}
          {...restProps}
          onChange={e => handleChange(e as React.ChangeEvent<HTMLInputElement>, onChange)}
          error={!!error}
          helperText={error?.message}
          suffix={suffix}
          fullWidth={fullWidth}
        />
      )}
    />
  )
}

NumberField.displayName = 'NumberField'

export default NumberField
